import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CellContext, ColumnDef, Row } from "@tanstack/react-table";
import DataTable from "@/components/controls/DataTable/DataTable";
import {
  GroupRankResolvers,
  statusRank
} from "@/entities/dataTables/groupOrdering";
import Text from "@/components/ui/text";
import { Filters } from "@/services/api/types";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import {
  getFullName,
  getSortableName,
  getSpecialPersonDisplayValue
} from "@/entities/people/helpers";
import {
  PersonColumn,
  PersonData,
  PersonReferenceabilityLevel,
  PersonStatus
} from "@/entities/people/types";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { createEnumSortingFn } from "@/utils/enums";
import { getEnumTKey } from "@/utils/strings";
import { usePeopleJobFunctions } from "@/entities/people/hooks";
import { LookupNameFromIdx } from "@/utils/datamaps";
import { simpleDate, toCalendarDate } from "@/utils/datetime";
import { ViewDataConfiguration } from "@/entities/views/types";
import { ColumnData } from "@/entities/dataTables/types";
import { StatusBadge } from "@/components/ui/status-badge";
import { GroupSummaryMap } from "@/components/controls/DataTable/groupSummaries";
import TableNameCell from "@/components/controls/TableNameCell";
import IcpFitValue from "@/components/controls/DetailedView/ICPFit/IcpFitValue";
import { IcpFit, IcpFitSubject } from "@/entities/icpFit/types";
import { ICP_FIT_COLUMN_ID } from "@/entities/icpFit/helpers";
import { UserData } from "@/entities/users/types";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import {
  DEAL_PEOPLE_TABLE_ID,
  CONTRACT_PEOPLE_TABLE_ID
} from "@/components/layouts/PeopleTab/constants";
import { useEntityFields, useFieldVisibility } from "@/pages/settings/hooks";
import {
  mapSelectedCustomColumnsToTableColumns,
  getTempView,
  excludeFieldsByKey,
  ICP_FIT_FIELD_KEYS
} from "@/entities/dataTables/helpers";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { cn } from "@/utils/utils";
import { getCurrentLocale } from "@/utils/locale";
import { getLocalizedCountryOptions } from "@/utils/countries";

type PersonStatusColorMap = {
  [K in PersonStatus]: string;
} & { null: string };

export const personStatusColors: PersonStatusColorMap = {
  [PersonStatus.NEW]: "var(--new)",
  [PersonStatus.GREENFIELD]: "var(--emerald-700)",
  [PersonStatus.ENGAGED_IN_DEAL]: "var(--link)",
  [PersonStatus.ENGAGED_IN_LEAD]: "var(--link)",
  [PersonStatus.ENGAGED]: "var(--link)",
  [PersonStatus.ACTIVE_CUSTOMER]: "var(--charts-positive)",
  [PersonStatus.DISQUALIFIED]: "var(--charts-negative)",
  null: "var(--border-field-default)"
};

// The column ids this table declares unconditionally. The flag-gated ones live
// outside the enum and are folded in per render — see `systemColumnIds`.
const ENUM_PERSON_COLUMN_IDS: readonly string[] = Object.values(PersonColumn);

interface PeopleTableProps {
  people: PersonData[];
  sorting: any;
  onSortingChange: (sorting: any) => void;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  tableId: string;
  selectedView?: ViewDataConfiguration;
  hiddenColumns?: string[];
  columnsState?: ColumnData[];
  onColumnsChange?: (updatedColumns?: ColumnData[]) => void;
  userData?: UserData;
  selectedPeople?: Set<string>;
  selectAll?: boolean;
  onPersonSelect?: (personId: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  isCompanyColumnSecond?: boolean;
  groupBy?: string[];
  /**
   * Every group of the whole filtered set with its server-side count, so a
   * group header does not climb as infinite scroll pages rows in. The page that
   * owns the query supplies them; a tab that never groups leaves them out.
   */
  groups?: Array<{ value: string | number; label: React.ReactNode }>;
  groupSummaries?: GroupSummaryMap;
  filterOptions?: DataTableFilter[];
  filters?: Filters;
  setFilters?: (filters: Filters | ((prev: Filters) => Filters)) => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  onClearSearch?: () => void;
  isError?: boolean;
  onRetry?: () => void;
  className?: string;
}

// Status groups follow the order its enum declares, not whichever column is sorted.
const GROUP_RANK_RESOLVERS: GroupRankResolvers<PersonData> = {
  [PersonColumn.STATUS]: statusRank
};

export const PeopleTable: React.FC<PeopleTableProps> = ({
  people,
  sorting,
  onSortingChange,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  tableId,
  selectedView,
  hiddenColumns = [],
  columnsState = [],
  onColumnsChange,
  userData,
  selectedPeople,
  selectAll,
  onPersonSelect,
  onSelectAll,
  isCompanyColumnSecond = false,
  groupBy = [],
  groups,
  groupSummaries,
  filterOptions,
  filters,
  setFilters,
  hasActiveFilters = false,
  onClearFilters,
  onClearSearch,
  isError = false,
  onRetry,
  className
}) => {
  const { t } = useTranslation();
  const { isFieldVisible, isLoaded: fieldsLoaded } = useFieldVisibility(
    EntityType.PERSON
  );
  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();

  const jobFunctions = usePeopleJobFunctions().data?.jobFunctions || [];

  const { data: entityFieldsResponse } = useEntityFields(EntityType.PERSON);
  const { enabled: isIcpFitEnabled } = useFeatureFlag(
    FeatureFlags.CRM_5676_ICP
  );
  const customFields = useMemo(() => {
    const fields = entityFieldsResponse?.fields || [];
    return isIcpFitEnabled
      ? fields
      : excludeFieldsByKey(fields, ICP_FIT_FIELD_KEYS);
  }, [entityFieldsResponse, isIcpFitEnabled]);

  // Every column id this table declares itself, the flag-gated ones included.
  // `mapSelectedCustomColumnsToTableColumns` drops a registry field whose
  // camelCased key is already in here, so an id left out of the set renders
  // twice: once as this table's own column, once from the entity-fields
  // registry. A flag-gated id belongs here only while its flag is on.
  const systemColumnIds = useMemo(
    () =>
      new Set<string>([
        ...ENUM_PERSON_COLUMN_IDS,
        ...(isIcpFitEnabled ? [ICP_FIT_COLUMN_ID] : [])
      ]),
    [isIcpFitEnabled]
  );

  const enableBulkActions =
    tableId === DEAL_PEOPLE_TABLE_ID || tableId === CONTRACT_PEOPLE_TABLE_ID;

  // Get selected columns from columnsState (updated by handleColumnsChange)
  // Fall back to temp view config if columnsState is empty (initial load)
  const selectedColumns = useMemo(() => {
    if (columnsState.length > 0) {
      return columnsState;
    }
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns || [];
  }, [columnsState, selectedView]);

  const onRowClick = useCallback(
    (person: PersonData) => {
      newOpenDrawer(person.id, EntityType.PERSON);
    },
    [newOpenDrawer]
  );

  const selectCell = useCallback(
    ({ row }: { row: any }) => {
      const isChecked = selectedPeople?.has(row.original.id) || false;
      return (
        <div
          className={cn(
            "!pl-[2px]",
            !isChecked && "opacity-0 group-hover:opacity-100 transition-opacity"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <SingleCheckbox
            id={`select-${row.original.id}`}
            isChecked={isChecked}
            onCheckedChange={(checked) =>
              onPersonSelect?.(row.original.id, checked)
            }
            label=""
            className="border-border-secondary-button"
            containerClassName="justify-center"
          />
        </div>
      );
    },
    [selectedPeople, onPersonSelect]
  );

  const companyColumn = {
    accessorKey: PersonColumn.COMPANY_NAME,
    header: t(`people.${PersonColumn.COMPANY_NAME}`),
    cell: (info: CellContext<PersonData, unknown>) =>
      info.getValue() || t("tables.notAvailable"),
    meta: { filterKey: "companyId" }
  };

  const countryOptions = useMemo(
    () => getLocalizedCountryOptions(getCurrentLocale()),
    []
  );

  const columns = useMemo<ColumnDef<PersonData>[]>(() => {
    const allColumns: ColumnDef<PersonData>[] = [
      ...(enableBulkActions
        ? [
            {
              id: "select",
              header: () => (
                <div className="pr-2" onClick={(e) => e.stopPropagation()}>
                  <SingleCheckbox
                    id="select-all"
                    isChecked={selectAll || false}
                    onCheckedChange={onSelectAll || (() => {})}
                    label=""
                    className="border-border-secondary-button"
                  />
                </div>
              ),
              cell: selectCell,
              size: 60,
              minSize: 60,
              maxSize: 60,
              enableSorting: false,
              enableResizing: false,
              meta: {
                textAlign: "center",
                paddingLeft: "pl-4",
                disableDragging: true,
                hideSeparator: true
              }
            }
          ]
        : []),
      {
        accessorKey: PersonColumn.FULL_NAME,
        accessorFn: (row) => getFullName(row),
        header: t(`people.${PersonColumn.FULL_NAME}`),
        cell: (info) => (
          <TableNameCell info={info} entityType={EntityType.PERSON} />
        ),
        sortingFn: (rowA, rowB, _columnId) => {
          const nameA = getSortableName(rowA.original);
          const nameB = getSortableName(rowB.original);
          return nameA.localeCompare(nameB);
        }
      },
      ...(isCompanyColumnSecond ? [companyColumn] : []),
      {
        accessorKey: PersonColumn.STATUS,
        header: t(`people.${PersonColumn.STATUS}`),
        sortingFn: createEnumSortingFn(PersonStatus),
        minSize: 170,
        cell: (info) => {
          const valueKey = getEnumTKey(
            "people",
            "statuses",
            PersonStatus,
            info.getValue() as PersonStatus
          );
          const statusKey = info.getValue() as PersonStatus;
          return (
            <StatusBadge statusColor={personStatusColors[statusKey]}>
              {t(valueKey)}
            </StatusBadge>
          );
        }
      },
      {
        accessorKey: PersonColumn.JOB_TITLE,
        header: t(`people.${PersonColumn.JOB_TITLE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PersonColumn.EMAIL,
        header: t(`people.${PersonColumn.EMAIL}`),
        cell: (info) => {
          const email = info.getValue() as string;
          return email ? (
            <div className="items-center justify-center">
              <Text
                variant="body"
                copyable
                copyLabels={{ tooltip: t("copyTooltip"), copied: t("copied") }}
              >
                {email}
              </Text>
            </div>
          ) : (
            t("tables.notAvailable")
          );
        }
      },
      {
        accessorKey: PersonColumn.PHONE,
        header: t(`people.${PersonColumn.PHONE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PersonColumn.DESK_PHONE,
        header: t(`people.${PersonColumn.DESK_PHONE}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PersonColumn.ADDRESS,
        header: t(`people.${PersonColumn.ADDRESS}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        accessorKey: PersonColumn.COUNTRY,
        header: t(`people.${PersonColumn.COUNTRY}`),
        cell: (info) => {
          const code = info.getValue() as string | null;
          if (!code) return t("tables.notAvailable");
          return (
            countryOptions.find((option) => option.code === code)?.label ??
            code
          );
        }
      },
      {
        accessorKey: PersonColumn.REPORTS_TO,
        header: t(`people.${PersonColumn.REPORTS_TO}`),
        cell: (info) => {
          const value = info.getValue() as string | null;
          const specialDisplayValue = getSpecialPersonDisplayValue(value, t);
          if (specialDisplayValue) return specialDisplayValue;
          const firstName = info?.row?.original?.reportsToFirstName || "";
          const lastName = info?.row?.original?.reportsToLastName || "";
          const person = { firstName, lastName };
          return getFullName(person);
        }
      },
      {
        accessorKey: PersonColumn.JOB_FUNCTION,
        header: t(`people.${PersonColumn.JOB_FUNCTION}`),
        cell: (info) =>
          LookupNameFromIdx(
            t,
            "people.jobFunctions",
            jobFunctions,
            info.getValue() as number
          ) || t("tables.notAvailable")
      },
      ...(!isCompanyColumnSecond ? [companyColumn] : []),
      {
        accessorKey: PersonColumn.CREATED_AT,
        header: t(`tables.${PersonColumn.CREATED_AT}`),
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      {
        accessorKey: PersonColumn.STATUS_CHANGED_AT,
        header: t(`tables.${PersonColumn.STATUS_CHANGED_AT}`),
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      {
        accessorKey: PersonColumn.REFERENCEABILITY,
        header: t(`people.${PersonColumn.REFERENCEABILITY}`),
        sortingFn: createEnumSortingFn(PersonReferenceabilityLevel),
        cell: (info) => {
          const value = info.getValue() as PersonReferenceabilityLevel | null;
          if (value == null) return t("tables.notAvailable");
          const valueKey = getEnumTKey(
            "people",
            "refLevels",
            PersonReferenceabilityLevel,
            value
          );
          return t(valueKey);
        }
      },
      // Match the custom-field pattern: include the ColumnDef only when the
      // column is toggled visible, so add/remove from Display remounts the
      // column (visibility-only toggling doesn't refresh the view). Kept while
      // it is the grouping target, as custom fields are.
      ...(isIcpFitEnabled &&
      (groupBy?.includes(ICP_FIT_COLUMN_ID) ||
        selectedColumns.some(
          (col: ColumnData) => col.id === ICP_FIT_COLUMN_ID && col.isVisible
        ))
        ? [
            {
              accessorKey: ICP_FIT_COLUMN_ID,
              header: t("people.icpFit"),
              sortingFn: "basic" as const,
              cell: (info: CellContext<PersonData, unknown>) => (
                <IcpFitValue
                  value={info.getValue() as IcpFit | null}
                  subject={IcpFitSubject.PERSON}
                  compact
                />
              )
            }
          ]
        : []),
      ...mapSelectedCustomColumnsToTableColumns(
        selectedColumns,
        customFields,
        t,
        systemColumnIds,
        groupBy
      )
    ];
    const visibleColumns = hiddenColumns.length
      ? allColumns.filter((column) => {
          const columnKey =
            "accessorKey" in column ? column.accessorKey : column.id;
          return !columnKey || !hiddenColumns.includes(String(columnKey));
        })
      : allColumns;
    return !fieldsLoaded
      ? visibleColumns
      : visibleColumns.filter((col) => {
          const key = (
            "accessorKey" in col ? col.accessorKey : col.id
          ) as string;
          return isFieldVisible(key);
        });
  }, [
    t,
    hiddenColumns,
    jobFunctions,
    enableBulkActions,
    selectCell,
    selectAll,
    onSelectAll,
    selectedColumns,
    customFields,
    systemColumnIds,
    isFieldVisible,
    fieldsLoaded,
    groupBy,
    isIcpFitEnabled,
    countryOptions
  ]);

  return (
    <>
      <DataTable
        groupRankResolvers={GROUP_RANK_RESOLVERS}
        key={tableId}
        dataTest={tableId}
        columns={columns}
        initialSortBy={sorting}
        manualSorting
        onSortingChange={onSortingChange}
        data={people}
        headerClassName="border-none"
        stickyLeftColumn
        resizableColumns
        isInfiniteScroll
        isLoadingMore={isFetchingNextPage}
        isDataLoading={isLoading}
        hasMore={hasNextPage}
        onLoadMore={onLoadMore}
        loadingThreshold={100}
        groupBy={groupBy}
        groupingConfig={{
          typeLabel: t("people.bottomKPI.total"),
          groups,
          groupSummaries
        }}
        enableColumnReorder
        tableId={tableId}
        selectedView={selectedView}
        columnsState={columnsState}
        onColumnsChange={onColumnsChange}
        onRowClick={(row: Row<any>) => onRowClick(row.original)}
        selectedRows={enableBulkActions ? selectedPeople : undefined}
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
        onClearSearch={onClearSearch}
        isError={isError}
        onRetry={onRetry}
        tableOptions={
          enableBulkActions
            ? {
                enableRowSelection: true,
                enableColumnResizing: true
              }
            : undefined
        }
        className={className}
      />
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.PERSON}
        onClose={newCloseDrawer}
        userData={userData}
        isTabView
      />
    </>
  );
};

export default PeopleTable;
